import type { TLanguage } from '../components/ui/language-picker';
import { EClaimType, ERefundOption } from './types';

const SUPPORTED_LANGUAGES: string[] = ['en', 'es', 'fr'] satisfies TLanguage[];
// Mirrors EntityType in services/direct, inlined to avoid pulling in its dependencies.
const ENTITY_TYPES = ['Individual', 'Business', 'Unknown'];
const CLAIM_TYPES: string[] = Object.values(EClaimType);
const PAYMENT_TYPES: string[] = Object.values(ERefundOption);

const SAFE_SEARCH_PARAMS = new Map<string, (value: string) => boolean>([
  ['language', (v) => SUPPORTED_LANGUAGES.includes(v)],
  ['entityType', (v) => ENTITY_TYPES.includes(v)],
  ['claimType', (v) => CLAIM_TYPES.includes(v)],
  ['paymentType', (v) => PAYMENT_TYPES.includes(v)],
  ['step', (v) => /^\d+$/.test(v)],
  ['view', (v) => v === 'inline' || v === 'attachment'],
  ['tangoType', (v) => v === 'giftcards' || v === 'donations'],
  ['country', (v) => /^[a-z]{2,3}$/i.test(v)],
]);

const REFUND_ID_PLACEHOLDER = ':refundId';
// Routes under /payments that sit beside /payments/$refundId.
const PAYMENTS_SUBROUTES = new Set(['payment-methods', 'thank-you']);
const API_PATHS_WITH_REFUND_ID = new Set([
  'generateotp',
  'matchotp',
  'get_logos',
  'get-logos-refundee',
]);

// The router and API match paths case-insensitively, and URL.pathname keeps percent escapes.
function normalizeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment).toLowerCase();
  } catch {
    return segment.toLowerCase();
  }
}

export function scrubPathname(pathname: string): string {
  const segments = pathname.split('/');
  const normalized = segments.map(normalizeSegment);

  return segments
    .map((segment, index) => {
      if (!segment) return segment;

      const previous = normalized[index - 1] ?? '';
      const isRefundRoute =
        index === 2 &&
        previous === 'payments' &&
        !PAYMENTS_SUBROUTES.has(normalized[index]);

      return isRefundRoute || API_PATHS_WITH_REFUND_ID.has(previous)
        ? REFUND_ID_PLACEHOLDER
        : segment;
    })
    .join('/');
}

const RELATIVE_SCRUB_BASE = 'https://relative.invalid';

function scrubRelativeUrl(raw: string): string {
  const isRooted = raw.startsWith('/');
  const scrubbed = scrubUrl(
    `${RELATIVE_SCRUB_BASE}${isRooted ? '' : '/'}${raw}`,
  ).slice(RELATIVE_SCRUB_BASE.length);

  return isRooted ? scrubbed : scrubbed.replace(/^\//, '');
}

export function scrubUrl(raw: string): string {
  if (!raw) return raw;

  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return scrubRelativeUrl(raw);
  }

  url.pathname = scrubPathname(url.pathname);

  for (const key of new Set(url.searchParams.keys())) {
    const isAllowedValue = SAFE_SEARCH_PARAMS.get(key);
    if (
      !isAllowedValue ||
      !url.searchParams.getAll(key).every(isAllowedValue)
    ) {
      url.searchParams.delete(key);
    }
  }

  url.username = '';
  url.password = '';
  url.hash = '';

  return url.toString();
}
